(() => {
  'use strict';
  const theme = window.theme;
  if (!theme || theme.settings?.themeName !== 'Impulse' || theme.settings?.themeVersion !== '10.0.0') return;
  if (theme.llProductFormStability) return;
  // Shopify serves a minified CDN form of the uploaded source. Accept only
  // either complete verified native profile, never mixed or app-owned methods.
  const expected = [{
    constructor: [192, 872783101],
    submitButton: [90, 2282950142],
    addItemFromForm: [1102, 804389486],
    success: [539, 705457103],
    error: [728, 647409033]
  }, {
    constructor: [168, 3919986435],
    submitButton: [82, 4254112701],
    addItemFromForm: [822, 290366372],
    success: [461, 3734206191],
    error: [641, 740248204]
  }];
  const fingerprint = fn => {
    if (typeof fn !== 'function') return null;
    const source = Function.prototype.toString.call(fn).replace(/\s+/g, ' ').trim();
    let hash = 2166136261;
    for (let index = 0; index < source.length; index++) hash = Math.imul(hash ^ source.charCodeAt(index), 16777619);
    return [source.length, hash >>> 0];
  };
  const same = (left, right) => !!left && left[0] === right[0] && left[1] === right[1];
  const mark = state => document.documentElement.setAttribute('data-ll-product-form-stability', state);
  const patch = ProductForm => {
    const prototype = ProductForm?.prototype;
    if (!prototype || !expected.some(profile => Object.entries(profile).every(([name, value]) =>
      same(fingerprint(name === 'constructor' ? ProductForm : prototype[name]), value)))) {
      mark('native-contract-changed');
      return;
    }
    let active = null;
    const uncertainMessage = theme.strings?.llProductFormStability?.uncertain || theme.strings?.unavailable || 'Unavailable';
    const addedMessage = theme.strings?.llProductFormStability?.addedCheckBag || uncertainMessage;
    const formSelector = '.product-single__form';
    const currentForm = form => {
      if (form.isConnected) return form;
      const id = form.getAttribute('id');
      const replacement = id && document.getElementById(id);
      return replacement?.matches(formSelector) ? replacement : null;
    };
    const echoDetachedError = form => {
      if (form.isConnected) return;
      const target = currentForm(form), error = form.querySelector('.errors');
      if (!target || !error) return;
      target.querySelector('.errors')?.remove();
      target.append(error.cloneNode(true));
    };
    const feedback = (form, message) => {
      const target = currentForm(form);
      if (!target) return;
      target.querySelector('.errors')?.remove();
      const error = document.createElement('div');
      error.classList.add('errors', 'text-center');
      error.setAttribute('role', 'alert');
      error.textContent = message;
      target.append(error);
    };
    const warn = error => console.warn('[LL product form] Callback failed', error);
    prototype.addItemFromForm = function(event, callback) {
      event.preventDefault();
      const form = this.form;
      if (active || !form?.isConnected || form.closest('[data-kit-refresh-busy]')) return Promise.resolve(false);
      const button = this.submitButton();
      if (button?.disabled) return Promise.resolve(false);
      const token = {};
      active = token;
      button?.classList.add('btn--loading');
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      let timer, callbackAttempted = false, handledResponse = false, refreshRequested = false;
      const refreshCart = () => {
        if (refreshRequested) return;
        refreshRequested = true;
        document.dispatchEvent(new CustomEvent('cart:build'));
      };
      const reportError = error => {
        callbackAttempted = true;
        try { this.error(error); echoDetachedError(form); } catch (failure) {
          warn(failure);
          feedback(form, typeof error.description === 'string' ? error.description : error.message || uncertainMessage);
        }
      };
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => {
          reject(new Error('Add request timed out'));
          controller?.abort();
        }, 20000);
      });
      // Snapshot the clicked form synchronously, matching native variant and
      // line-property semantics even if a later listener replaces controls.
      const request = new Promise(resolve => resolve(new FormData(form))).then(body => {
        return fetch(theme.routes.cartAdd, {
          method: 'POST', body,
          headers: {'X-Requested-With': 'XMLHttpRequest'},
          ...(controller ? {signal: controller.signal} : {})
        });
      }).then(async response => {
        const data = await response.json();
        const status = Number(data?.status || response.status);
        if (status === 422) {
          const hasDescription = typeof data?.description === 'string' && data.description.trim().length > 0;
          const hasMessage = typeof data?.message === 'string' && data.message.trim().length > 0;
          if (!data || typeof data !== 'object' || Array.isArray(data) || (!hasDescription && !hasMessage)) {
            throw new Error('Add error response did not contain feedback');
          }
          const fieldErrors = hasMessage && data.description && typeof data.description === 'object' &&
            !Array.isArray(data.description) && Object.keys(data.description).length > 0;
          return {kind: 'stock-error', error: {
            ...data,
            description: hasDescription || fieldErrors ? data.description : data.message,
            message: hasMessage ? data.message : data.description
          }};
        }
        if (response.ok === false || status >= 400) throw new Error('Add request failed');
        const validItem = item => item && typeof item === 'object' && /^\d+$/.test(String(item.id)) && Number(item.id) > 0;
        if (!validItem(data) && !(Array.isArray(data?.items) && data.items.length && data.items.every(validItem))) {
          throw new Error('Add response did not contain an item');
        }
        return {kind: 'success', product: data};
      });
      return Promise.race([request, timeout]).then(result => {
        handledResponse = true;
        if (result.kind === 'stock-error') {
          const error = result.error?.description ? result.error : {
            ...result.error, description: result.error?.message || uncertainMessage
          };
          reportError(error);
          // Shopify can partially add available stock before returning422.
          // Preserve the native cart-refresh event exactly once.
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
          return false;
        }
        callbackAttempted = true;
        const connectedBeforeSuccess = form.isConnected;
        try {
          this.success(result.product);
          if (!connectedBeforeSuccess) {
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
              detail: {product: result.product, addToCartBtn: currentForm(form)?.querySelector('.add-to-cart') || button}
            }));
          }
        } catch (failure) { warn(failure); feedback(form, addedMessage); refreshCart(); }
        return true;
      }).catch(() => {
        if (!callbackAttempted) reportError({message: uncertainMessage, description: uncertainMessage});
        refreshCart();
        return false;
      }).finally(() => {
        clearTimeout(timer);
        if (active === token) active = null;
        try {
          button?.classList.remove('btn--loading');
          form.querySelector('.add-to-cart')?.classList.remove('btn--loading');
          currentForm(form)?.querySelector('.add-to-cart')?.classList.remove('btn--loading');
        } catch (failure) { warn(failure); }
        if (handledResponse && document.body.classList.contains('template-cart')) {
          window.scrollTo(0, 0);
          location.reload();
        }
      });
    };
    // Theme/app section replacement can leave a valid new form without its
    // native binding. Already handled or app-owned submits remain untouched.
    document.addEventListener('submit', event => {
      const form = event.target;
      if (event.defaultPrevented || theme.AjaxProduct !== ProductForm || !form?.matches?.(formSelector) ||
          !form.closest('.product-section')) return;
      const instance = new ProductForm(form);
      instance.addItemFromForm(event);
    });
    theme.llProductFormStability = {version: '10.0.0', installed: true};
    mark('installed');
  };
  if (theme.AjaxProduct) { patch(theme.AjaxProduct); return; }
  const descriptor = Object.getOwnPropertyDescriptor(theme, 'AjaxProduct');
  if (descriptor && !descriptor.configurable) { mark('native-contract-unavailable'); return; }
  Object.defineProperty(theme, 'AjaxProduct', {
    configurable: true, enumerable: true, get: () => undefined,
    set: ProductForm => {
      Object.defineProperty(theme, 'AjaxProduct', {value: ProductForm, configurable: true, enumerable: true, writable: true});
      patch(ProductForm);
    }
  });
})();
